import React from 'react';
import { TrendingDown, Target, Users, DollarSign } from 'lucide-react';

const MarketingBurnRatePanel = ({ data, onChange, calculations, formatCurrency }) => {
  const parseNum = (val) => {
    const num = parseFloat(String(val)?.replace(/[^\d.-]/g, ''));
    return isNaN(num) ? 0 : num;
  };

  const formatInput = (val) => {
    if (!val) return '';
    return String(val);
  };

  const totalCampaignBudget = 
    parseNum(data?.campaignBudgets?.social) +
    parseNum(data?.campaignBudgets?.search) +
    parseNum(data?.campaignBudgets?.display) +
    parseNum(data?.campaignBudgets?.email);

  const estimatedCustomers = data?.customerAcquisitionCost > 0 
    ? totalCampaignBudget / parseNum(data?.customerAcquisitionCost)
    : 0;

  const roiTimeline = data?.customerAcquisitionCost > 0 && data?.monthlyRevenue > 0
    ? (parseNum(data?.customerAcquisitionCost) / (parseNum(data?.monthlyRevenue) / 30))?.toFixed(1)
    : 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
          <TrendingDown className="w-5 h-5 text-rose-600" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-slate-900">Marketing Burn Rate</h3>
          <p className="text-xs text-slate-600">Monthly marketing spend & CAC analysis</p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Social Media Budget
          </label>
          <input
            type="text"
            value={formatInput(data?.campaignBudgets?.social)}
            onChange={(e) => onChange('campaignBudgets', 'social', e?.target?.value)}
            className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all text-sm"
            placeholder="Monthly social budget"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Search Advertising Budget
          </label>
          <input
            type="text"
            value={formatInput(data?.campaignBudgets?.search)}
            onChange={(e) => onChange('campaignBudgets', 'search', e?.target?.value)}
            className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all text-sm"
            placeholder="Monthly search budget"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Display Advertising Budget
          </label>
          <input
            type="text"
            value={formatInput(data?.campaignBudgets?.display)}
            onChange={(e) => onChange('campaignBudgets', 'display', e?.target?.value)}
            className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all text-sm"
            placeholder="Monthly display budget"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Email Marketing Budget
          </label>
          <input
            type="text"
            value={formatInput(data?.campaignBudgets?.email)}
            onChange={(e) => onChange('campaignBudgets', 'email', e?.target?.value)}
            className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all text-sm"
            placeholder="Monthly email budget"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Customer Acquisition Cost (CAC)
          </label>
          <input
            type="text"
            value={formatInput(data?.customerAcquisitionCost)}
            onChange={(e) => onChange(null, 'customerAcquisitionCost', e?.target?.value)}
            className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all text-sm"
            placeholder="Average cost per customer"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Conversion Rate (%)
          </label>
          <input
            type="number"
            value={data?.conversionRate}
            onChange={(e) => onChange(null, 'conversionRate', e?.target?.value)}
            className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all text-sm"
            min="0"
            max="100"
            step="0.1"
          />
        </div>

        {/* Marketing Performance Metrics */}
        <div className="mt-6 pt-6 border-t border-slate-200">
          <h4 className="text-sm font-bold text-slate-900 mb-4">Performance Metrics</h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-slate-600" />
                <span className="text-sm text-slate-600">Total Marketing Spend</span>
              </div>
              <strong className="text-base text-slate-900">
                {formatCurrency(totalCampaignBudget)}
              </strong>
            </div>
            
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-600" />
                <span className="text-sm text-slate-600">Est. Customers</span>
              </div>
              <strong className="text-base text-slate-900">
                {estimatedCustomers?.toFixed(0)}
              </strong>
            </div>

            <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">ROI Timeline</span>
              </div>
              <strong className="text-base text-blue-700">
                {roiTimeline} days
              </strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingBurnRatePanel;